/**
 * CreateLeadDialog — F2 manual lead entry.
 *
 * Required by form: First/Last name, Processor, AND at least one of Phone/Email.
 * Sales Rep, Lead Status (defaults to "new_lead"), Lead Types, Notes are optional.
 * Backend POST /api/leads is lenient (consistent with F1 "imports stay lenient,
 * banner surfaces gaps later"). This form is the strict UI entry path.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import UserAssignSelect from "@/components/UserAssignSelect";

const LEAD_STATUSES = [
    { value: "new_lead",   label: "New Lead" },
    { value: "paid_lead",  label: "Paid Lead" },
    { value: "follow_up",  label: "Follow Up" },
    { value: "shark_tank", label: "Shark Tank" },
    { value: "hot_lead",   label: "Hot Lead" },
];

const EMPTY = {
    first_name: "",
    last_name: "",
    phone_raw: "",
    email: "",
    processor_user_id: null,
    processor_name: "",
    processor_email: "",
    sales_rep_user_id: null,
    sales_rep_name: "",
    sales_rep_email: "",
    lead_status: "new_lead",
    lead_type_ids: [],
    notes: "",
};

export default function CreateLeadDialog({ open, onOpenChange, onCreated }) {
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [leadTypes, setLeadTypes] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(EMPTY);
        api.get("/lead-types").then(r => {
            const items = r.data?.items || r.data || [];
            setLeadTypes(items.filter(t => t.active !== false));
        }).catch(() => setLeadTypes([]));
    }, [open]);

    const errors = useMemo(() => {
        const e = {};
        if (!form.first_name.trim()) e.first_name = "Required";
        if (!form.last_name.trim()) e.last_name = "Required";
        if (!form.phone_raw.trim() && !form.email.trim()) {
            e.contact = "Either Phone or Email is required.";
        }
        if (!form.processor_user_id) e.processor = "Processor is required.";
        return e;
    }, [form]);

    const canSubmit = Object.keys(errors).length === 0 && !busy;

    const toggleType = (id) => {
        setForm(f => ({
            ...f,
            lead_type_ids: f.lead_type_ids.includes(id)
                ? f.lead_type_ids.filter(x => x !== id)
                : [...f.lead_type_ids, id],
        }));
    };

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const { data } = await api.post("/leads", {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                phone_raw: form.phone_raw.trim(),
                email: form.email.trim() || null,
                processor_user_id: form.processor_user_id,
                processor_name: form.processor_name,
                processor_email: form.processor_email,
                sales_rep_user_id: form.sales_rep_user_id,
                sales_rep_name: form.sales_rep_name,
                sales_rep_email: form.sales_rep_email,
                lead_status: form.lead_status,
                lead_type_ids: form.lead_type_ids,
                notes: form.notes.trim() || null,
            });
            const newId = data.id;
            toast.success("Lead created");
            onOpenChange(false);
            onCreated?.(data);
            if (newId) navigate(`/leads/${newId}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="create-lead-dialog">
                <DialogHeader>
                    <DialogTitle>Create Lead</DialogTitle>
                    <DialogDescription>Manually add a new lead. Processor is required.</DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">First Name <span className="text-rose-600">*</span></Label>
                        <Input
                            value={form.first_name}
                            onChange={e => setForm({ ...form, first_name: e.target.value })}
                            data-testid="create-lead-first-name"
                            aria-invalid={!!errors.first_name}
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Last Name <span className="text-rose-600">*</span></Label>
                        <Input
                            value={form.last_name}
                            onChange={e => setForm({ ...form, last_name: e.target.value })}
                            data-testid="create-lead-last-name"
                            aria-invalid={!!errors.last_name}
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Phone</Label>
                        <Input
                            value={form.phone_raw}
                            onChange={e => setForm({ ...form, phone_raw: e.target.value })}
                            placeholder="(555) 555-5555"
                            data-testid="create-lead-phone"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Email</Label>
                        <Input
                            value={form.email}
                            type="email"
                            onChange={e => setForm({ ...form, email: e.target.value })}
                            placeholder="name@example.com"
                            data-testid="create-lead-email"
                        />
                    </div>
                    {errors.contact && (
                        <div className="col-span-2 text-xs text-rose-600" data-testid="create-lead-contact-error">{errors.contact}</div>
                    )}

                    <div className="col-span-2">
                        <Label className="text-xs">Processor <span className="text-rose-600">*</span></Label>
                        <UserAssignSelect
                            value={form.processor_user_id}
                            onSelect={(u) => setForm({
                                ...form,
                                processor_user_id: u?.id || null,
                                processor_name: u?.name || "",
                                processor_email: u?.email || "",
                            })}
                            placeholder="Assign processor…"
                            testId="create-lead-processor"
                            allowUnassigned={false}
                            triggerClassName="h-9 text-sm w-full"
                        />
                        {errors.processor && (
                            <div className="text-xs text-rose-600 mt-1" data-testid="create-lead-processor-error">{errors.processor}</div>
                        )}
                    </div>

                    <div className="col-span-2">
                        <Label className="text-xs">Sales Rep</Label>
                        <UserAssignSelect
                            value={form.sales_rep_user_id}
                            onSelect={(u) => setForm({
                                ...form,
                                sales_rep_user_id: u?.id || null,
                                sales_rep_name: u?.name || "",
                                sales_rep_email: u?.email || "",
                            })}
                            placeholder="Assign sales rep (optional)…"
                            testId="create-lead-sales-rep"
                            triggerClassName="h-9 text-sm w-full"
                        />
                    </div>

                    <div className="col-span-2">
                        <Label className="text-xs">Lead Status</Label>
                        <Select value={form.lead_status} onValueChange={(v) => setForm({ ...form, lead_status: v })}>
                            <SelectTrigger className="h-9 text-sm" data-testid="create-lead-status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {leadTypes.length > 0 && (
                        <div className="col-span-2">
                            <Label className="text-xs">Lead Types</Label>
                            <div className="mt-1 grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto border border-border rounded-md p-2" data-testid="create-lead-types">
                                {leadTypes.map(t => (
                                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <Checkbox
                                            checked={form.lead_type_ids.includes(t.id)}
                                            onCheckedChange={() => toggleType(t.id)}
                                            data-testid={`create-lead-type-${t.id}`}
                                        />
                                        <span>{t.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="col-span-2">
                        <Label className="text-xs">Notes</Label>
                        <Textarea
                            value={form.notes}
                            onChange={e => setForm({ ...form, notes: e.target.value })}
                            rows={3}
                            placeholder="Any context that helps the team…"
                            data-testid="create-lead-notes"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-lead-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={!canSubmit} data-testid="create-lead-submit">
                        {busy ? "Creating…" : "Create Lead"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
